"use client";

import React from "react";
import Link from "next/link";

const CookiePolicy: React.FC = () => {
  return (
    <div className="decimal-list-alignment container py-4 py-md-5">
      <div className="row justify-content-center">
        <div className="col-24">
          <section className="mb-4">
            <ol className="decimal-zero-list" style={{ counterReset: "item 0" }}>
              <li>
                <div className="decimal-list-heading d-flex d-md-block">
                  <div className="h2 d-md-none me-2">1.0</div>
                  <h2 className="text-uppercase">Purpose</h2>
                </div>
                <p className="mb-0">
                  The purpose of this policy is to explain to users of this website{" "}
                  <Link href="/">www.bulletproof.co.uk</Link> how cookies are used
                  on the site, the options for controlling the use of cookies and
                  what cookies are used and for what purpose.
                </p>
              </li>
            </ol>
          </section>

          <section className="mb-4">
            <ol className="decimal-zero-list" style={{ counterReset: "item 1" }}>
              <li>
                <div className="decimal-list-heading d-flex d-md-block">
                  <div className="h2 d-md-none me-2">2.0</div>
                  <h2 className="text-uppercase">Scope</h2>
                </div>
                <p className="mb-0">
                  When we provide services via our website this sometimes involves
                  placing small amounts of information on the device that you are
                  using to be able to fully access our website. This policy applies
                  to the cookies used on this website www.bulletproof.co.uk and to
                  enable a service to recognise your device to prevent you from
                  having to provide the same information during one task, or when
                  you revisit our website.
                </p>
              </li>
            </ol>
          </section>

          <section className="mb-4">
            <ol className="decimal-zero-list" style={{ counterReset: "item 2" }}>
              <li>
                <div className="decimal-list-heading d-flex d-md-block">
                  <div className="h2 d-md-none me-2">3.0</div>
                  <h2 className="text-uppercase">Definitions</h2>
                </div>
                <p>The following terms are used throughout this policy and are defined as follows:</p>
                <ul className="list-unstyled ms-0 p-0">
                  {[
                    {
                      term: "Cookie",
                      definition:
                        "Cookies are small files that are placed on your computer by the websites that you visit. They are used in order to make websites work, to provide features, or to work more efficiently, as well as to provide information to the owner of the site such as pages visited, number of visitors etc. Some cookies also enable the display of relevant advertising when you move from site to site.",
                    },
                    {
                      term: "We/us/our",
                      definition:
                        "refers to Bulletproof Cyber Ltd the owner of this website registered at Unit H, Gateway 1000, Whittle Way, Stevenage, Herts, SG1 2FP. UK with company registration number 05490180.",
                    },
                    {
                      term: "Personal data",
                      definition:
                        "is any information which can directly or indirectly identify a living person as defined under Article 4 (1) of the UK General Data Protection Regulations (UK GDPR)",
                    },
                    {
                      term: "First Party Cookie",
                      definition: "this is a cookie that is controlled by us",
                    },
                    {
                      term: "Third Party Cookie",
                      definition:
                        "this is a cookie provided by a third party e.g. Google Analytics for which we have no control over",
                    },
                    {
                      term: "Session",
                      definition:
                        "If a cookie does not contain an expiration date, it is considered a session cookie. Session cookies are stored in memory and never written to disk. When the browser closes, the cookie is permanently lost from this point on.",
                    },
                    {
                      term: "Persistent",
                      definition:
                        "these cookies stay in one of your browser's subfolders until you delete them manually or your browser deletes them based on the duration period contained within the persistent cookie's file",
                    },
                  ].map(({ term, definition }, index) => (
                    <li key={index} className="p-0">
                      <p className="mb-0">
                        <span className="fw-bolder">{`"${term}"`}</span> – {definition}
                      </p>
                    </li>
                  ))}
                </ul>
              </li>
            </ol>
          </section>

          <section className="mb-4">
            <ol className="decimal-zero-list" style={{ counterReset: "item 3" }}>
              <li>
                <div className="decimal-list-heading d-flex d-md-block">
                  <div className="h2 d-md-none me-2">4.0</div>
                  <h2 className="text-uppercase">Cookie types</h2>
                </div>
                <p>We use the following types of cookies on our website:</p>
                <ul className="list-unstyled ms-0 p-0">
                  {[
                    {
                      type: "Necessary Cookies",
                      description:
                        "These cookies are necessary for the operation of our site. They don't access or store any personal data. Our website cannot function without these cookies so they are, by default switched on to ensure you can experience the full functionality of our website.",
                    },
                    {
                      type: "Analytics Cookies",
                      description:
                        "These cookies allow us to analyze website use such as which pages are most popular and how visitors move around the site. The information collected does not directly identify any individuals.",
                    },
                    {
                      type: "Functionality Cookies",
                      description:
                        "These cookies allow us to recognize you when you return to our website. They enable us to personalize content for you and remember preferences you may have set on your last visit.",
                    },
                    {
                      type: "Performance/statistics cookies",
                      description:
                        "These cookies collect information about how you use our website (e.g. which Internet browser you use, how often you visit our website, which pages you open, or how long you stay on our website). These cookies do not store any information that enables visitors to be personally identified. The information collected with the help of these cookies is aggregated, and thus anonymous.",
                    },
                    {
                      type: "Marketing Cookies",
                      description:
                        "these cookies allow us to provide personalized and relevant services or advertising to our visitors, and to assess the effectiveness of our digital marketing activities. We also use these cookies to enable third party 'plug-ins' like social media sharing and like buttons. They also enable us to play video content.",
                    },
                  ].map(({ type, description }, index) => (
                    <li key={index} className="p-0">
                      <p className="mb-0">
                        <span className="fw-bolder">{type}</span> – {description}
                      </p>
                    </li>
                  ))}
                </ul>

                <ol className="decimal-list">
                  <li className="default-marker-style">
                    <h3 className="text-uppercase mb-4 fs-4">
                      <span className="d-md-none me-1">4.1</span> Necessary cookies
                    </h3>
                    <table className="table-brand mb-2">
                      <thead>
                      <tr>
                        <th>Cookie Name</th>
                        <th>Type</th>
                        <th>Duration</th>
                        <th style={{ width: '50%' }}>Purpose</th>
                      </tr>
                      </thead>
                      <tbody>
                      {[
                        {
                          name: "__cfduid",
                          type: "Persistent",
                          duration: "30 days",
                          purpose:
                            "Cookie associated with sites using CloudFlare, used to speed up page load times. According to CloudFlare it is used to override any security restrictions based on the IP address the visitor is coming from. It does not contain any user identification information.",
                        },
                        {
                          name: "_bulletproof_session",
                          type: "Session",
                          duration: "Expire once you close your browser",
                          purpose:
                            "Session cookies allow bulletproof.co.uk to remember users when they move between web pages. These cookies tell the server what pages to show the user so the user doesn't have to remember where they left off or start navigating the site all over again. Therefore, without session cookies, websites have no memory.",
                        },
                        {
                          name: "_ga",
                          type: "Persistent",
                          duration: "730 days",
                          purpose:
                            "This cookie name is associated with Google Universal Analytics – which is a significant update to Google's more commonly used analytics service. This cookie is used to distinguish unique users by assigning a randomly generated number as a client identifier. It is included in each page request in a site and used to calculate visit session and campaign data for the sites analytics reports.",
                        },
                        {
                          name: "OptanonAlertBoxClosed",
                          type: "Persistent",
                          duration:
                            "The cookie has a one year lifespan and contains no personal information.",
                          purpose:
                            "This cookie is set after visitors have seen a cookie information notice and, in some cases, only when they actively close the notice down. It enables the website not to show the message more than once to a user.",
                        },
                      ].map((cookie, index) => (
                        <tr key={index}>
                          <td data-th="Cookie name">{cookie.name}</td>
                          <td data-th="Cookie type">
                            <span className="text-uppercase">{cookie.type}</span>
                          </td>
                          <td data-th="Cookie duration">{cookie.duration}</td>
                          <td data-th="Purpose">{cookie.purpose}</td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  </li>
                  <li className="default-marker-style">
                    <h3 className="text-uppercase mb-4 fs-4"><span className="d-md-none me-1">4.3</span> Analytics
                      cookies</h3>
                    <table className="table-brand mb-2">
                      <thead>
                      <tr>
                        <th>Cookie Name</th>
                        <th>Type</th>
                        <th>Duration</th>
                        <th style={{ width: '50%' }}>Purpose</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr>
                        <td data-th="Cookie name">_ga</td>
                        <td data-th="Cookie type"><span className="text-uppercase">Persistent</span></td>
                        <td data-th="Cookie duration">730 days</td>
                        <td data-th="Purpose">This cookie name is associated with Google Universal Analytics – which is
                          a significant update to Google’s more commonly used analytics service. This cookie is used to
                          distinguish unique users by assigning a randomly generated number as a client identifier. It
                          is included in each page request in a site and used to calculate visitor, session and campaign
                          data for the sites analytics reports.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">_gat_UA-</td>
                        <td data-th="Cookie type"><span className="text-uppercase">Persistent</span></td>
                        <td data-th="Cookie duration">Expire once you close your browser</td>
                        <td data-th="Purpose">This is a pattern type cookie set by Google Analytics, where the pattern
                          element on the name contains the unique identity number of the account or website it relates
                          to. It appears to be a variation of the _gat cookie which is used to limit the amount of data
                          recorded by Google on high traffic volume websites.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">_gclxxxx</td>
                        <td data-th="Cookie type"><span className="text-uppercase">Persistent</span></td>
                        <td data-th="Cookie duration">90 days</td>
                        <td data-th="Purpose">-This cookie name is associated with Google Universal Analytics, and it is
                          used to throttle the request rate – limiting the collection of data on high traffic sites.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">_gid</td>
                        <td data-th="Cookie type"><span className="text-uppercase">Persistent</span></td>
                        <td data-th="Cookie duration">90 days</td>
                        <td data-th="Purpose">This cookie is used for Google Analytics to store and update a unique
                          value for each page visited.
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </li>
                  <li className="default-marker-style">
                    <h3 className="text-uppercase mb-4 fs-4"><span className="d-md-none me-1">4.4</span> Marketing
                      cookies</h3>
                    <p><strong>Note:</strong> This includes sales and marketing emails. </p>
                    <table className="table-brand mb-0">
                      <thead>
                      <tr>
                        <th>Cookie Name</th>
                        <th>Type</th>
                        <th>Duration</th>
                        <th style={{ width: '50%' }}>Purpose</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr>
                        <td data-th="Cookie name">_fbp</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">90 days</td>
                        <td data-th="Purpose">Used by Facebook to deliver a series of advertisement products such as
                          real time bidding from third party advertisers.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">_gat_gtag_xxxxxxxxxxxxxxxxxxxxxxxxxxx</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">Expire once you close your browser</td>
                        <td data-th="Purpose">Google Analytics</td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">_rdt_uuid</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">90 days</td>
                        <td data-th="Purpose">This cookie is set by Reddit and is used for remarketing on reddit.com
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">_uetsid</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">1 day</td>
                        <td data-th="Purpose">This cookie is used by Bing to determine what ads should be shown that may
                          be relevant to the end user perusing the site.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">AnalyticsSyncHistory</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">30 days</td>
                        <td data-th="Purpose">This domain is owned by LinkedIn, the business networking platform. It
                          typically acts as a third party host where website owners have placed one of its content
                          sharing buttons in their pages, although its content and services can be embedded in other
                          ways. Although such buttons add functionality to the website they are on, cookies are set
                          regardless of whether or not the visitor has an active Linkedin profile, or agreed to their
                          terms and conditions. For this reason it is classified as a primarily tracking/targeting
                          domain.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">bscookie</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">731 days</td>
                        <td data-th="Purpose">This domain is owned by LinkedIn, the business networking platform. It
                          typically acts as a third party host where website owners have placed one of its content
                          sharing buttons in their pages, although its content and services can be embedded in other
                          ways. Although such buttons add functionality to the website they are on, cookies are set
                          regardless of whether or not the visitor has an active Linkedin profile, or agreed to their
                          terms and conditions. For this reason it is classified as a primarily tracking/targeting
                          domain.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">CONSENT</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">729 days</td>
                        <td data-th="Purpose">YouTube is a Google owned platform for hosting and sharing videos. YouTube
                          collects user data through videos embedded in websites, which is aggregated with profile data
                          from other Google services in order to display targeted advertising to web visitors across a
                          broad range of their own and other websites.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">fr</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">90 days</td>
                        <td data-th="Purpose">&quot;fr&quot; cookie is used to deliver, measure and improve the relevancy of ads,
                          with a lifespan of 90 days.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">GPS</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">Expire once you close your browser</td>
                        <td data-th="Purpose">Detecting a unique ID on mobile devices to enable tracking based on
                          geographic GPS location.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">IDE</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">390 days</td>
                        <td data-th="Purpose">Used for targeting purposes to build a profile of the website visitor’s
                          interests in order to show relevant & personalised advertising
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">lang</td>
                        <td data-th="Cookie type" className="text-uppercase">Session</td>
                        <td data-th="Cookie duration">Expire once you close your browser</td>
                        <td data-th="Purpose">This domain is owned by LinkedIn, the business networking platform. It
                          typically acts as a third party host where website owners have placed one of its content
                          sharing buttons in their pages, although its content and services can be embedded in other
                          ways. Although such buttons add functionality to the website they are on, cookies are set
                          regardless of whether or not the visitor has an active Linkedin profile, or agreed to their
                          terms and conditions. For this reason it is classified as a primarily tracking/targeting
                          domain.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">li_gc</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">725 days</td>
                        <td data-th="Purpose">This domain is owned by LinkedIn, the business networking platform. It
                          typically acts as a third party host where website owners have placed one of its content
                          sharing buttons in their pages, although its content and services can be embedded in other
                          ways. Although such buttons add functionality to the website they are on, cookies are set
                          regardless of whether or not the visitor has an active Linkedin profile, or agreed to their
                          terms and conditions. For this reason it is classified as a primarily tracking/targeting
                          domain.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">lidc</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">1 day</td>
                        <td data-th="Purpose">This domain is owned by LinkedIn, the business networking platform. It
                          typically acts as a third party host where website owners have placed one of its content
                          sharing buttons in their pages, although its content and services can be embedded in other
                          ways. Although such buttons add functionality to the website they are on, cookies are set
                          regardless of whether or not the visitor has an active Linkedin profile, or agreed to their
                          terms and conditions. For this reason it is classified as a primarily tracking/targeting
                          domain.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">lissc</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">365 days</td>
                        <td data-th="Purpose">Used for routing from LinkedIn Share buttons & advertising services.</td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">MUID</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">390 days</td>
                        <td data-th="Purpose">This domain is owned by Microsoft – it is the site for the search engine
                          Bing.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">reddaid</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">730 days</td>
                        <td data-th="Purpose">Saves the information about log-on Reddit user for the purpose of the
                          advertisement recommendation and updating the content, the content of the cookie is confirmed
                          within the conditions of the Reddit use.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">SRM_B</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">389 days</td>
                        <td data-th="Purpose">This domain is owned by Microsoft – it is the site for the search engine
                          Bing.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">test_cookie</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">Expire once you close your browser</td>
                        <td data-th="Purpose">Used by Google to check if your browser supports cookies.</td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">UserMatchHistory</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">30 days</td>
                        <td data-th="Purpose">This domain is owned by LinkedIn, the business networking platform. It
                          typically acts as a third party host where website owners have placed one of its content
                          sharing buttons in their pages, although its content and services can be embedded in other
                          ways. Although such buttons add functionality to the website they are on, cookies are set
                          regardless of whether or not the visitor has an active Linkedin profile, or agreed to their
                          terms and conditions. For this reason it is classified as a primarily tracking/targeting
                          domain.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">YSC</td>
                        <td data-th="Cookie type" className="text-uppercase">Session</td>
                        <td data-th="Cookie duration">Expire once you close your browser</td>
                        <td data-th="Purpose">YouTube is a Google owned platform for hosting and sharing videos. YouTube
                          collects user data through videos embedded in websites, which is aggregated with profile data
                          from other Google services in order to display targeted advertising to web visitors across a
                          broad range of their own and other websites.
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">ANONCHK</td>
                        <td data-th="Cookie type" className="text-uppercase">Persistent</td>
                        <td data-th="Cookie duration">Expire once you close your browser</td>
                        <td data-th="Purpose">This cookie is used by Microsoft for storing the session ID for a user.
                          This cookie ensures that clicks from advertisement on the Bing search engine are verified and
                          it is used for reporting purposes and for personalization.
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </li>
                  <li className="default-marker-style">
                    <h3 className="text-uppercase mb-4 fs-4"><span className="d-md-none me-1">4.5</span> Consent Mode
                    </h3>
                    <table className="table-brand mb-4">
                      <thead>
                      <tr>
                        <th>Cookie Name</th>
                        <th>Type</th>
                        <th>Duration</th>
                        <th style={{ width: '50%' }}>Purpose</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr>
                        <td data-th="Cookie name">Ad_storage</td>
                        <td data-th="Cookie type">Google</td>
                        <td data-th="Cookie duration">Retained only by Google</td>
                        <td data-th="Purpose">Controls advertising cookies usage</td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">Analytics_storage</td>
                        <td data-th="Cookie type">Google</td>
                        <td data-th="Cookie duration">Retained only by Google</td>
                        <td data-th="Purpose">Controls analytics cookies usage</td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">Ad_user_data</td>
                        <td data-th="Cookie type">Google</td>
                        <td data-th="Cookie duration">Retained only by Google</td>
                        <td data-th="Purpose">Is used to determine whether the user gives consent for google
                          advertisement purposes
                        </td>
                      </tr>
                      <tr>
                        <td data-th="Cookie name">Ad_personalization</td>
                        <td data-th="Cookie type">Google</td>
                        <td data-th="Cookie duration">Retained only by Google</td>
                        <td data-th="Purpose">Controls whether data can be used for ads personalisation</td>
                      </tr>
                      </tbody>
                    </table>
                    <p>Further details can be found on Google link: <Link
                      href="https://support.google.com/tagmanager/answer/10718549?hl=en"
                      target="_blank">https://support.google.com/tagmanager/answer/10718549?hl=en</Link></p>
                  </li>
                </ol>
              </li>
            </ol>
          </section>
          <section className="mb-4">
            <ol className="decimal-zero-list" style={{ counterReset: "item 4" }}>
              <li>
                <div className="decimal-list-heading d-flex d-md-block">
                  <div className="h2 d-md-none me-2">5.0</div>
                  <h2 className="text-uppercase">COOKIE MANAGEMENT</h2>
                </div>
                <ol className="decimal-list">
                  <li className="default-marker-style">
                    <h3 className="text-uppercase mb-4 fs-4"><span className="d-md-none me-1">5.1</span> WEBSITE CONTROL
                    </h3>
                    <p>When you access our website, you will receive a notification informing you that our website uses
                      cookies along with an option of whether to browse using cookies along with a link to our Privacy
                      Policy.</p>
                    <p>You have the ability to accept or decline cookies. By default, all cookies, except necessary
                      cookies, are disabled. By accepting the cookies, you consent to the processing of data about them
                      by Bulletproof for the purposes set out above.</p>
                    <p>You can choose not to use cookies by not accepting the option and still access our website
                      without enabling the cookies. However, in a few cases, some of our website features may not
                      function as a result.</p>
                  </li>
                  <li className="default-marker-style">
                    <h3 className="text-uppercase mb-4 fs-4"><span className="d-md-none me-1">5.2</span> BROWSER CONTROL
                    </h3>
                    <p>You also have the ability to accept or decline cookies through their internet browser software
                      and can modify these settings to accept, decline and remove cookies if you prefer. Your choices
                      will include the ability to switch off cookies completely and removing cookies by deleting them
                      when you leave our site. If you do switch cookies off you may find problems with the operation of
                      our website. To delete cookies select | Tools button, Safety, delete browser history and
                      instructions on how to do so, for some of the most common browsers, are detailed below. </p>
                    <ul className="list-unstyled p-0 ms-0 mb-0">
                      {[
                        {
                          name: "Google Chrome",
                          url: "https://support.google.com/chrome/answer/95647?co=GENIE.Platform%3DDesktop&hl=en",
                        },
                        {
                          name: "Firefox",
                          url: "https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop?redirectslug=enable-and-disable-cookies-website-preferences&redirectlocale=en-US",
                        },
                        {
                          name: "Safari(Mac)",
                          url: "https://support.apple.com/en-gb/guide/safari/sfri11471/mac",
                        },
                        {
                          name: "Safari(iPhone/iPad)",
                          url: "https://support.apple.com/en-us/105082",
                        },
                        {
                          name: "Internet Explorer",
                          url: "https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d",
                        },
                        {
                          name: "Microsoft Edge",
                          url: "https://support.microsoft.com/en-us/search?query=enable%20cookies%20in%20edge",
                        },
                      ].map((browser, index) => (
                        <li key={index} className="p-0">
                          <a
                            href={browser.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {browser.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="default-marker-style">
                    <h3 className="text-uppercase mb-4 fs-4"><span className="d-md-none me-1">5.3</span> ANONYMOUS
                      TRACKING </h3>
                    <p>You can opt out of anonymous tracking from third party analytics cookies by clicking on the links
                      of our service providers here:</p>
                    <p>Google Analytics: <Link href="https://tools.google.com/dlpage/gaoptout"
                                               target="_blank">https://tools.google.com/dlpage/gaoptout</Link></p>
                  </li>
                </ol>
              </li>
            </ol>
          </section>
          <section className="mb-4">
            <ol className="decimal-zero-list" style={{ counterReset: "item 5" }}>
              <li>
                <div className="decimal-list-heading d-flex d-md-block">
                  <div className="h2 d-md-none me-2">6.0</div>
                  <h2 className="text-uppercase">FURTHER INFORMATION</h2>
                </div>
                <ol className="decimal-list">
                  <li className="default-marker-style">
                    <h3 className="text-uppercase mb-4 fs-4"><span className="d-md-none me-1">6.1</span> CONTACT US</h3>
                    <p>If you would like any further information about the use of cookies on this website, please
                      contact us at <Link href="mailto:dpo@bulletproof.co.uk">dpo@bulletproof.co.uk</Link></p>
                  </li>
                  <li className="default-marker-style">
                    <h3 className="text-uppercase mb-4 fs-4"><span className="d-md-none me-1">6.2</span> ADDITIONAL
                      NOTICES</h3>
                    <p>It is important that you read this privacy notice together with any other, more specific, privacy
                      notices we may provide when we are collecting or processing personal data about you, so that you
                      are fully aware of how and why we are using your personal data.</p>
                    <p>Our website Privacy Notice is available at <Link
                      href="https://www.bulletproof.co.uk/privacy-notice"
                      target="_blank">https://www.bulletproof.co.uk/privacy-notice</Link></p>
                  </li>
                  <li className="default-marker-style">
                    <h3 className="text-uppercase mb-4 fs-4"><span className="d-md-none me-1">6.2</span> Changes to this
                      policy</h3>
                    <p>We reserve the right to make changes to this policy from time to time and the latest version of
                      the policy will always be displayed on our website. You should check this policy from time to time
                      to see any updates.</p>
                    <p className="mb-0">This policy was last updated on 21/03/2024. </p>
                  </li>
                </ol>
              </li>
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;